import {
  STORAGE_KEY,
  THREAD_ID_STORAGE_KEY,
  CURRENT_SESSION_KEY,
  SESSIONS_METADATA_KEY,
  scopedStorageKey,
  type StorageScope,
} from '../constants/storage';
import { getSessionKey, type SessionMetadata } from './sessionHistory';
import { clearThreadToken } from './threadCapability';
import type { SessionConfig } from '../types';

function storage(config?: SessionConfig): Storage | null {
  try {
    return config?.storage === 'session' ? sessionStorage : localStorage;
  } catch {
    return null;
  }
}

/** Removes this widget's browser transcript and its thread capability, without touching other tenants. */
export function forgetStoredConversation(
  scope: StorageScope,
  config?: SessionConfig,
  sessionId?: string | null,
  threadId?: string | null
): void {
  const store = storage(config);
  clearThreadToken(threadId, config);
  if (!store) return;
  try {
    const storedThread = store.getItem(scopedStorageKey(THREAD_ID_STORAGE_KEY, scope));
    clearThreadToken(storedThread, config);
    store.removeItem(scopedStorageKey(STORAGE_KEY, scope));
    store.removeItem(scopedStorageKey(THREAD_ID_STORAGE_KEY, scope));
    if (sessionId) {
      store.removeItem(getSessionKey(sessionId));
      const key = scopedStorageKey(SESSIONS_METADATA_KEY, scope);
      const list = JSON.parse(store.getItem(key) || '[]') as SessionMetadata[];
      if (Array.isArray(list))
        store.setItem(key, JSON.stringify(list.filter(item => item.id !== sessionId)));
      if (store.getItem(scopedStorageKey(CURRENT_SESSION_KEY, scope)) === sessionId)
        store.removeItem(scopedStorageKey(CURRENT_SESSION_KEY, scope));
    }
  } catch {
    /* Storage may be unavailable. */
  }
}

/** Enforces inactivity-based retention before thread/history hydration. Undefined preserves existing policy. */
export function pruneStoredConversations(
  scope: StorageScope,
  config?: SessionConfig,
  retentionDays?: number,
  now = Date.now()
): boolean {
  if (!Number.isFinite(retentionDays) || retentionDays! < 0) return false;
  const store = storage(config);
  if (!store) return false;
  let expiredActive = false;
  const cutoff = now - retentionDays! * 86400000;
  try {
    const key = scopedStorageKey(SESSIONS_METADATA_KEY, scope);
    const sessions = JSON.parse(store.getItem(key) || '[]') as SessionMetadata[];
    if (Array.isArray(sessions)) {
      for (const session of sessions) {
        if ((Date.parse(session.lastModified) || 0) > cutoff) continue;
        const data = JSON.parse(store.getItem(getSessionKey(session.id)) || 'null');
        clearThreadToken(data?.threadId, config);
        store.removeItem(getSessionKey(session.id));
      }
      store.setItem(
        key,
        JSON.stringify(sessions.filter(item => (Date.parse(item.lastModified) || 0) > cutoff))
      );
      const currentKey = scopedStorageKey(CURRENT_SESSION_KEY, scope);
      const current = store.getItem(currentKey);
      if (current && !store.getItem(getSessionKey(current))) {
        forgetStoredConversation(scope, config, current);
        expiredActive = true;
      }
    }
    const historyKey = scopedStorageKey(STORAGE_KEY, scope);
    const raw = store.getItem(historyKey);
    if (raw) {
      const messages = JSON.parse(raw);
      const latest = Array.isArray(messages)
        ? Math.max(0, ...messages.map(item => Date.parse(item.timestamp) || 0))
        : 0;
      if (latest <= cutoff) {
        forgetStoredConversation(scope, config);
        expiredActive = true;
      }
    }
  } catch {
    forgetStoredConversation(scope, config);
    expiredActive = true;
  }
  return expiredActive;
}
